import {
    COSTS_DIRECT_CAMPUS_FEES,
    COSTS_DIRECT_HEALTH_INSURANCE,
    COSTS_DIRECT_HOUSING_AND_MEALS,
    COSTS_DIRECT_TUITION,
    COSTS_INDIRECT_BOOKS_AND_SUPPLIES,
    COSTS_INDIRECT_HOUSING_AND_MEALS,
    COSTS_INDIRECT_PERSONAL_AND_TRANSPORTATION,
    ERROR_HOUSING,
    ERROR_RESIDENCY,
    ERROR_STATUS,
    HAS_DEPENDENTS_NO,
    HOUSING_COMMUTER,
    HOUSING_OFF_CAMPUS,
    HOUSING_ON_CAMPUS,
    INDEX_DIRECT,
    INDEX_INDIRECT,
    RESIDENCY_CALIFORNIA,
    RESIDENCY_OTHER,
    STATUS_DEPENDENT,
    STATUS_INDEPENDENT
} from "../steps/bi/constants/MAGIC_NUMBERS";
import {FormulaA} from "../steps/bi/saiFormulas/formulaA";
import {FormulaB} from "../steps/bi/saiFormulas/formulaB";
import {FormulaC} from "../steps/bi/saiFormulas/formulaC";
import {ATTENDANCE_COSTS} from "../steps/bi/constants/ATTENDANCE_COSTS";
import {useContext} from "react";
import {ReactReduxContext} from "react-redux";

/**
 * CheckStep4 method checks the data from previous steps and returns an object
 * containing the status and value for dependent, housing, and residency.
 *
 * @returns {Object} - An object with the following properties:
 * - dependent_pass: A boolean indicating if the dependent status is valid.
 * - dependent_value: The value of the dependent status.
 * - housing_pass: A boolean indicating if the housing status is valid.
 * - housing_value: The value of the housing status.
 * - residency_pass: A boolean indicating if the residency status is valid.
 * - residency_value: The value of the residency status.
 */
export function CheckStep4() {
    const context = useContext(ReactReduxContext);
    const data = () => context.store.getState().app.stepData;
    const val = data();

    const data1 = val.step1;
    const checkDependent = data1.status !== undefined && !isNaN(data1.status) &&
        (data1.status === STATUS_DEPENDENT || data1.status === STATUS_INDEPENDENT);
    const statusDependent = checkDependent ? data1.status : ERROR_STATUS;

    const data2 = val.step2;
    const checkHousing = data2.housing !== undefined && !isNaN(data2.housing) &&
        (
            data2.housing === HOUSING_ON_CAMPUS ||
            data2.housing === HOUSING_OFF_CAMPUS ||
            data2.housing === HOUSING_COMMUTER
        );
    const statusHousing = checkHousing ? data2.campus : ERROR_HOUSING;

    const checkResidency = data2.residency !== undefined && !isNaN(data2.residency) &&
        (data2.residency === RESIDENCY_CALIFORNIA || data2.residency === RESIDENCY_OTHER);
    const statusResidency = checkResidency ? data2.residency : ERROR_RESIDENCY;

    return {
        dependent_pass: checkDependent,
        dependent_value: statusDependent,
        housing_pass: checkHousing,
        housing_value: statusHousing,
        residency_pass: checkResidency,
        residency_value: statusResidency
    };
}

/**
 * Calculates the SAI (Student Aid Index) based on the given data and checks.
 * The SAI is calculated differently depending on the status of the student.
 *
 * @param {Object} data - The data object containing the information needed for calculations.
 * @param {Object} checks - The checks object containing additional information for calculations.
 * @returns {number} - The calculated SAI (Student Aid Index).
 */
const step4CalculateSAI = (data, checks) => {

    if(data.step2.residency === RESIDENCY_OTHER) {
        return 0;
    }

    return (data.step1.status === STATUS_DEPENDENT)
        ? FormulaA(data)
        : (data.step1.status === STATUS_INDEPENDENT)
            ? (data.step3.studentHasDependents === HAS_DEPENDENTS_NO)
                ? FormulaB(data)
                : FormulaC(data)
            : 0 //  We really shouldn't be able to get here, but if someone manages to make status some other value, let's zero it out
};
/**
 * Returns the tuition cost for attending UC based on campus.
 *
 * @param {Object} d1 - The input data object which contains indices for lookup.
 * @returns {number} - The retrieved tuition cost.

 */
const step4CalculateUCTuition = (d1) => {
    const data = d1.step2;
    return ATTENDANCE_COSTS[data.residency][data.campus][COSTS_DIRECT_TUITION][data.housing];
};
/**
 * Returns campus fees based on campus.
 *
 * @param {Object} d1 - The input data object which contains indices for lookup.
 * @returns {number} - The retrieved campus fees.
 */
const step4CalculateCampusFees = (d1) => {
    const data = d1.step2;
    return ATTENDANCE_COSTS[data.residency][data.campus][COSTS_DIRECT_CAMPUS_FEES][data.housing];
};
/**
 * Returns health insurance cost per campus
 *
 * @param {object} d1 - The input data object which contains indices for lookup.
 * @returns {number} - The retrieved health insurance cost.
 */
const step4CalculateHealthInsurance = (d1) => {
    const data = d1.step2;
    return ATTENDANCE_COSTS[data.residency][data.campus][COSTS_DIRECT_HEALTH_INSURANCE][data.housing];
};
/**
 * Returns the housing and meals costs based on campus and housing type.
 *
 * @param {object} d1 - The input data object which contains indices for lookup.
 * @returns {Object} - An object containing the retrieved housing and meals costs.
 *                    The object has two properties:
 *                      - INDEX_DIRECT: The direct housing and meals costs. (On campus)
 *                      - INDEX_INDIRECT: The indirect housing and meals costs. (Other housing)
 */
const step4CalculateHousingAndMeals = (d1) => {
    const data = d1.step2;
    return {
        [INDEX_DIRECT]: ATTENDANCE_COSTS[data.residency][data.campus][COSTS_DIRECT_HOUSING_AND_MEALS][data.housing],
        [INDEX_INDIRECT]: ATTENDANCE_COSTS[data.residency][data.campus][COSTS_INDIRECT_HOUSING_AND_MEALS][data.housing]
    };
};
/**
 * Returns the cost of books and supplies based on campus.
 *
 * @param {object} d1 - The input data object which contains indices for lookup.
 * @returns {number} - The retrieved cost of books and supplies.
 */
const step4CalculateBooksAndSupplies = (d1) => {
    const data = d1.step2;
    return ATTENDANCE_COSTS[data.residency][data.campus][COSTS_INDIRECT_BOOKS_AND_SUPPLIES][data.housing];
};
/**
 * Returns the cost of indirect personal and transportation expenses based on campus and housing.
 *
 * @param {object} d1 - The input data object which contains indices for lookup.
 * @returns {number} - The retrieved cost of indirect personal and transportation expenses.
 */
const step4CalculatePersonalAndTransportation = (d1) => {
    const data = d1.step2;
    return ATTENDANCE_COSTS[data.residency][data.campus][COSTS_INDIRECT_PERSONAL_AND_TRANSPORTATION][data.housing];
};

export function GetCosts(data) {
    const checks = CheckStep4();
    checks["step2"] = data.step2;
    checks['data'] = data;

    return {
        checks: checks,
        estimatedStudentAidIndex: step4CalculateSAI(data, checks),
        ucTuition: step4CalculateUCTuition(data),
        campusFees: step4CalculateCampusFees(data),
        healthInsurance: step4CalculateHealthInsurance(data),
        housingAndMeals: step4CalculateHousingAndMeals(data),
        booksAndSupplies: step4CalculateBooksAndSupplies(data),
        personalAndTransportation: step4CalculatePersonalAndTransportation(data),
    }
}
